import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { attendanceService } from '../../services/attendance.service';
import { FaClock, FaSignInAlt, FaSignOutAlt } from 'react-icons/fa';

const MyAttendance = () => {
  const [attendance, setAttendance] = useState([]);
  const [todayStatus, setTodayStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAttendance();
  }, []);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const data = await attendanceService.getMyAttendance();
      const attendanceRecords = data.attendance || [];
      setAttendance(attendanceRecords);
      
      // Find today's record
      const today = attendanceRecords.find(a => {
        const recordDate = new Date(a.date).toDateString();
        const currentDate = new Date().toDateString();
        return recordDate === currentDate;
      });
      
      setTodayStatus(today || null);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Failed to fetch attendance');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (todayStatus && !todayStatus.checkOut) {
      toast.warning('You have already checked in today!');
      return;
    }

    try {
      setActionLoading(true);
      await attendanceService.checkIn({});
      toast.success('Checked in successfully!');
      await fetchAttendance(); // Refresh data
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error(error.response?.data?.message || 'Failed to check in');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!todayStatus) {
      toast.error('Please check in first!');
      return;
    }

    if (todayStatus.checkOut) {
      toast.warning('You have already checked out today!');
      return;
    }

    try {
      setActionLoading(true);
      await attendanceService.checkOut({});
      toast.success('Checked out successfully!');
      await fetchAttendance(); // Refresh data
    } catch (error) {
      console.error('Check-out error:', error);
      toast.error(error.response?.data?.message || 'Failed to check out');
    } finally {
      setActionLoading(false);
    }
  };

  const canCheckIn = () => {
    return !todayStatus || (todayStatus && todayStatus.checkOut);
  };

  const canCheckOut = () => {
    return todayStatus && todayStatus.checkIn && !todayStatus.checkOut;
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">My Attendance</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Today's Status Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <FaClock className="text-blue-600" />
            Today's Status
          </h2>
          {todayStatus ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Status:</span>
                <span className={`px-3 py-1 rounded font-semibold ${
                  todayStatus.status === 'present' ? 'bg-green-100 text-green-800' :
                  todayStatus.status === 'late' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {todayStatus.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Check-in:</span>
                <span className="font-medium">{new Date(todayStatus.checkIn).toLocaleTimeString()}</span>
              </div>
              {todayStatus.checkOut ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Check-out:</span>
                    <span className="font-medium">{new Date(todayStatus.checkOut).toLocaleTimeString()}</span>
                  </div>
                 
                  <div className="bg-green-50 border border-green-200 rounded p-3 mt-4">
                    <p className="text-sm text-green-800 font-medium">✓ You have completed your attendance for today</p>
                  </div>
                </>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-4">
                  <p className="text-sm text-blue-800">You are currently checked in. Don't forget to check out!</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <FaClock className="text-6xl text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Not checked in today</p>
              <p className="text-sm text-gray-400 mt-1">Click the check-in button to mark your attendance</p>
            </div>
          )}
        </div>

        {/* Actions Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <button
              onClick={handleCheckIn}
              disabled={!canCheckIn() || actionLoading}
              className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                canCheckIn() && !actionLoading
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <FaSignInAlt />
              {actionLoading ? 'Processing...' : 'Check In'}
            </button>

            <button
              onClick={handleCheckOut}
              disabled={!canCheckOut() || actionLoading}
              className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                canCheckOut() && !actionLoading
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <FaSignOutAlt />
              {actionLoading ? 'Processing...' : 'Check Out'}
            </button>

            <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
              <p className="font-medium mb-1">Instructions:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Check in when you start work</li>
                <li>Check out when you finish work</li>
                <li>You can only check in once per day</li>
                <li>Make sure to check out before leaving</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Attendance History */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">Attendance History</h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading history...</p>
          </div>
        ) : attendance.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No attendance records found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check In</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check Out</th>
                  
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {attendance.map((record) => (
                  <tr key={record._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{new Date(record.date).toLocaleDateString()}</td>
                    <td className="px-6 py-4">{new Date(record.checkIn).toLocaleTimeString()}</td>
                    <td className="px-6 py-4">
                      {record.checkOut ? new Date(record.checkOut).toLocaleTimeString() : '-'}
                    </td>
                    
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        record.status === 'present' ? 'bg-green-100 text-green-800' :
                        record.status === 'late' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {record.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAttendance;